"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useLocale } from "next-intl"
import { Fragment } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem } from "utils"
import { handleAnchorScroll } from "../scrollToAnchor"
import PowerNumberStat, { PowerNumberStatData } from "./PowerNumberStat"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

const MAX_ITEMS = 4

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }
`

const Inner = styled.div`
  width: 100%;
  max-width: 1200px;
  text-align: start;
`

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 48px;
  }
`

/* first in JSX = right side in RTL */
const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    max-width: 500px;
    flex-shrink: 0;
  }
`

const TextColumn = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 640px;
  }
`

const Text = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
    line-height: 30px;
  }
`

const CtaButton = styled(Button)`
  && {
    height: 40px;
    margin-top: 24px;
    padding: 0 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    ${(props) => props.theme.breakpoints.up("md")} {
      height: 48px;
      padding: 0 32px;
      font-size: 20px;
    }

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }
  }
`

const StatsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 40px;
  /* 'start' (not physical 'right'), which the emotion RTL plugin would flip to left —
     keeps stats right-aligned in RTL on both breakpoints (ASH-1474, ASH-1575) */
  text-align: start;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 0;
    margin-top: 56px;
  }
`

/* mobile: vertical divider on the right (RTL start) edge of each stat.
   desktop: divider handled by <StatDivider> between cells. */
const StatCell = styled.div`
  position: relative;
  padding-inline-start: 16px;

  &::before {
    content: "";
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: var(--comp-text);
    opacity: 0.25;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    /* spacing now comes from StatDivider's margin, so the cell adds none */
    padding: 0;

    &::before {
      display: none;
    }
  }
`

const StatDivider = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
    width: 1px;
    align-self: stretch;
    background-color: var(--comp-text);
    opacity: 0.25;
    /* The divider is a flex child, so a row gap would apply on both sides of
       it and double the spacing. Carrying half the 48px gap as margin keeps
       cell-to-cell spacing at the Figma value (ASH-1473). */
    margin: 0 24px;
  }
`

// --- Parsing ---

function parseItem(block: any): PowerNumberStatData {
  return {
    number: (extractProperty(block, "number") as string) || "",
    numberCaption: (extractProperty(block, "numberCaption") as string) || "",
  }
}

function isEmptyItem(item: PowerNumberStatData): boolean {
  return !item.number && !item.numberCaption
}

// --- Main Component ---

function PowerNumbers2({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale

  const headline = extractProperty(data, "headline") as string | ""
  const text = extractProperty(data, "text") as string | ""
  const ctaLinks = extractProperty(data, "ctaButton") as any[] | ""
  const cta = Array.isArray(ctaLinks) ? ctaLinks[0] : undefined
  const itemBlocks = extractProperty(data, "items") as any[] | ""
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => !isEmptyItem(item))
    .slice(0, MAX_ITEMS)

  if (!headline && !text && items.length === 0) return null

  // MUI styled(Button) doesn't type target/rel; apply them via a spread when the link opens a new tab
  const ctaLinkProps: Record<string, string> =
    cta?.target === "_blank" ? { target: "_blank", rel: "noopener noreferrer" } : {}

  return (
    <Section dir={isRtl ? "rtl" : "ltr"}>
      <Inner>
        <HeaderRow>
          {headline && <Headline>{headline}</Headline>}
          {(text || (cta?.url && cta?.name)) && (
            <TextColumn>
              {text && <Text>{text}</Text>}
              {cta?.url && cta?.name && (
                <CtaButton
                  variant="contained"
                  href={getLinkHrefFromMultiUrlPickerItem(cta)}
                  onClick={(e: React.MouseEvent) => {
                    analytics.ctaClick(cta.name, LANDING_ELEMENTS.powerNumbers)
                    handleAnchorScroll(e, getLinkHrefFromMultiUrlPickerItem(cta))
                  }}
                  disableElevation
                  {...ctaLinkProps}
                >
                  {cta.name}
                </CtaButton>
              )}
            </TextColumn>
          )}
        </HeaderRow>
        {items.length > 0 && (
          <StatsRow>
            {items.map((item, i) => (
              <Fragment key={i}>
                {i > 0 && <StatDivider aria-hidden />}
                <StatCell>
                  <PowerNumberStat stat={item} variant="mode2" />
                </StatCell>
              </Fragment>
            ))}
          </StatsRow>
        )}
      </Inner>
    </Section>
  )
}

export default PowerNumbers2
